import { SAVE_HASH } from '../systems/saveTransfer';
import { confirmAndImportPlayer } from './ui/importPlayer';
import Phaser from 'phaser';
import {
  ANIMAL_FRAME_COUNTS,
  animalAssetKeys,
  animalFrameKey,
  DEFAULT_ANIMAL,
} from '../art/AnimalSprite';
import { ANIMAL_IDS } from '../data/animals';
import { artResolution } from '../art/artScale';
import { forgetArtKey, MANIFEST_REGISTRY, queueArt } from '../art/loadArt';
import { showLoadingBar } from './ui/loadingBar';
import { backgroundLayerKey } from '../data/themes';
import { ensurePlaceholderTexture, ensurePlaceholders } from '../art/placeholders';
import {
  devLevelFromQuery,
  devResultsRequested,
  devSampleStars,
  devSceneFromQuery,
  devTideRequested,
  devUnlockFromQuery,
} from '../systems/devQuery';
import { PLATFORM_VARIANTS } from './game/Tower';
import { PALETTE } from '../types';
import { startTideRun } from '../systems/TideRun';
import type { SaveManager } from '../systems/SaveManager';
import type {
  AssetKey,
  AssetManifest,
  BackgroundTheme,
  GameRunStats,
  PlatformTheme,
} from '../types';

const MANIFEST_KEY = 'asset-manifest';

/**
 * Keys the game references today. Anything the manifest resolves to a file is
 * loaded as a real SVG; anything it cannot satisfy is generated as a
 * placeholder texture under the same key, so a half-finished asset group makes
 * the game look unfinished rather than break it.
 */
/** The title stands in the sunset (src/scenes/Title.ts). */
const TITLE_ENV: BackgroundTheme = 'sunset';

export const BACKGROUND_LAYERS = ['sky', 'far', 'near'] as const;

const PLATFORM_THEMES: readonly PlatformTheme[] = [
  'ice-floe',
  'cloud',
  'marshmallow',
  'soccer-field',
  'game-day',
];

function coreAssetKeys(): AssetKey[] {
  const keys: AssetKey[] = [
    'ui/logo',
    'ui/star_full',
    'ui/star_empty',
    'ui/lock',
    'ui/button',
    'platforms/start',
    'platforms/goal',
    'powerups/spring_icon',
    'powerups/jetpack_icon',
    'powerups/spring_coil',
    'powerups/jetpack_worn',
    // Background layers are not listed here: src/art/environments.ts draws
    // them per environment, and a generic placeholder under the same key
    // would shadow it. A manifest file takes priority over both.
  ];

  // Three variants per platform theme (docs/ART_SPEC.md).
  for (const theme of PLATFORM_THEMES) {
    for (let variant = 1; variant <= PLATFORM_VARIANTS; variant += 1) {
      keys.push(`platforms/${theme}/platform_${variant}`);
    }
  }

  // Every frame of the starting animal, body and details.
  keys.push(...animalAssetKeys(DEFAULT_ANIMAL));

  // The animal grid draws one idle frame for every animal, locked or not, so
  // those keys need a texture of their own or Phaser draws its missing-texture
  // square. The other frames of an animal are made on demand by AnimalSprite
  // once that animal is actually chosen.
  for (const id of ANIMAL_IDS) {
    if (id === DEFAULT_ANIMAL) continue;
    for (let frame = 0; frame < ANIMAL_FRAME_COUNTS.idle; frame += 1) {
      keys.push(animalFrameKey(id, 'idle', frame, 'body'));
      keys.push(animalFrameKey(id, 'idle', frame, 'details'));
    }
  }

  return keys;
}

export class Preload extends Phaser.Scene {
  // Phaser reuses scene instances, so these are reset in preload(), not here.
  private manifest: AssetManifest | null = null;
  private queuedFromManifest = 0;
  private startedAt = 0;

  constructor() {
    super('Preload');
  }

  preload(): void {
    this.manifest = null;
    this.queuedFromManifest = 0;
    this.startedAt = performance.now();

    this.cameras.main.setBackgroundColor(PALETTE.sky);

    // The manifest is the single source of truth for keys -> files (CLAUDE.md).
    // It sits under /assets, which hosts cache for a year, so each build asks
    // for it by a new URL; the art it lists carries its own content hashes.
    this.load.json(MANIFEST_KEY, `assets/manifest.json?v=${String(import.meta.env.VITE_BUILD_ID)}`);
    // The boot load always shows its bar straight away: there is nothing else
    // on screen yet for it to flash over.
    showLoadingBar(this, { revealMs: 0 });

    // Files added while the loader is still running join the same pass, so the
    // art is queued the moment the manifest itself arrives and the progress bar
    // still measures the whole load.
    this.load.once(`filecomplete-json-${MANIFEST_KEY}`, () => {
      this.manifest = this.readManifest();
      this.registry.set(MANIFEST_REGISTRY, this.manifest);
      this.queuedFromManifest = this.queueManifestAssets(this.manifest);
    });
  }

  /**
   * Queue what the menus need, and no more.
   *
   * Every animal's full frame set plus all ten environments came to 236 files
   * and 426MB of texture memory, nearly all of it art this run would never
   * show. Game loads the animal and environment its own level uses.
   */
  private queueManifestAssets(manifest: AssetManifest | null): number {
    if (manifest === null) return 0;

    const keys = new Set<AssetKey>(coreAssetKeys());
    // The title stands in the sunset, so its layers are the one environment
    // worth having before a level picks its own.
    for (const layer of BACKGROUND_LAYERS) keys.add(backgroundLayerKey(TITLE_ENV, layer));

    return queueArt(this, keys);
  }

  create(): void {
    const manifest = this.manifest;
    // Built here, not at import: animal keys depend on the art style, which
    // main.ts sets after this module has loaded.
    const core = coreAssetKeys();
    const generated = ensurePlaceholders(this, core, manifest);

    // A manifest entry whose file failed to load would otherwise leave its key
    // with no texture at all. Falling back beats drawing nothing.
    const rescued = core.filter((key) => !this.textures.exists(key));
    for (const key of rescued) {
      // Forget the resolution recorded when the file was queued: the fallback
      // is a 1x placeholder, and leaving the high-res divisor in place would
      // draw it at a fraction of its size.
      forgetArtKey(this, key);
      ensurePlaceholderTexture(this, key);
    }

    // A key the manifest promised but whose file never arrived is a real
    // problem, so it is named rather than counted, and warned about in any
    // build rather than only in dev.
    if (rescued.length > 0) {
      console.warn(
        `[Preload] ${rescued.length} manifest asset(s) failed to load and fell back to ` +
          `placeholders: ${rescued.join(', ')}`,
      );
    }

    if (import.meta.env.DEV) {
      console.info(
        `[Preload] ${this.queuedFromManifest} file(s) from manifest at ` +
          `${artResolution(this).toFixed(2)}x, ` +
          `${generated.length} placeholder texture(s) generated, ` +
          `${Math.round(performance.now() - this.startedAt)}ms.`,
      );
    }

    // A scanned QR code or save link opens the game with #save=<code>.
    if (this.importFromLink()) return;

    // Dev shortcut: ?level=N goes straight to the climb, because the menus
    // that would otherwise lead there are not built until Prompt 7.
    const devScene = devSceneFromQuery();
    if (devScene !== null) {
      this.seedDevProfile();
      this.scene.start(devScene);
      return;
    }

    if (devResultsRequested()) {
      // Results reads the unlock from the registry, exactly as Game sets it.
      this.registry.set('newUnlock', devUnlockFromQuery());
      this.scene.start('Results', sampleRunStats(devSampleStars()));
      return;
    }

    const devLevel = devLevelFromQuery();
    if (devTideRequested()) {
      const level = devLevel ?? 1;
      this.scene.start('Game', { level, tide: startTideRun(level, []) });
      return;
    }
    if (devLevel !== null) {
      this.scene.start('Game', { level: devLevel });
      return;
    }

    this.scene.start('Title');
  }

  /**
   * Bring in a player from a #save= link, then head for their map. The hash is
   * cleared first, so a reload or a bookmark never offers it again.
   * Returns true when it has started the next scene itself.
   */
  private importFromLink(): boolean {
    const hash = window.location.hash;
    if (!hash.startsWith(SAVE_HASH)) return false;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    const save: unknown = this.registry.get('save');
    if (save === undefined || save === null) return false;
    const outcome = confirmAndImportPlayer(save as SaveManager, hash);
    if (outcome !== 'imported') return false;
    this.scene.start('LevelMap');
    return true;
  }

  /**
   * The ?scene= routes skip ProfileSelect, so give them a profile with some
   * progress: a few levels done and a few stars, enough for the map and the
   * animal grid to show both states. Dev only.
   */
  private seedDevProfile(): void {
    const save: unknown = this.registry.get('save');
    if (save === undefined || save === null) return;
    const manager = save as SaveManager;
    if (manager.activeProfile() !== null) return;

    manager.addProfile(0, 'Dev');
    // Screenshots and QA go straight to the screen they asked for.
    manager.markHelpersSeen();

    // Two weeks of plausible practice: speed and accuracy climbing with a dip
    // or two, the same few words and keys missed again and again, so the
    // grown-up screen has a real shape to review rather than a flat line.
    const wpm = [7.2, 7.9, 7.4, 8.6, 9.1, 8.3, 9.8, 10.4, 9.9, 11.2, 11.8, 12.6];
    const accuracy = [84, 86, 83, 88, 89, 87, 91, 92, 90, 93, 95, 96];
    const missed = [
      ['brain', 'friend'],
      ['friend', 'quiet'],
      ['brain'],
      ['quiet', 'their'],
      ['friend'],
      ['their', 'weird'],
      ['brain', 'quiet'],
      [],
      ['weird'],
      ['friend'],
      [],
      ['their'],
    ];
    const keys: Array<Record<string, number>> = [
      { r: 3, e: 2, i: 2 },
      { i: 2, q: 1 },
      { r: 2, a: 1 },
      { e: 2, h: 1 },
      { i: 1, r: 1 },
      { e: 2, w: 1 },
      { q: 1, r: 1, u: 1 },
      { e: 1 },
      { w: 1, i: 1 },
      { r: 1 },
      {},
      { h: 1 },
    ];
    const day = 24 * 60 * 60 * 1000;
    const start = Date.now() - wpm.length * day;
    wpm.forEach((speed, i) => {
      const level = 1 + Math.floor(i / 2);
      const acc = accuracy[i] ?? 90;
      manager.recordRun(
        {
          level,
          platformCount: 20,
          reachedGoal: true,
          wordsAttempted: 19,
          wordsCorrect: 18,
          keystrokes: 80,
          keystrokeHits: Math.round(80 * (acc / 100)),
          accuracy: acc,
          wordAccuracy: 94,
          longestStreak: 11,
          elapsedMs: 72000,
          score: 380,
          stars: acc >= 95 ? 3 : acc >= 85 ? 2 : 1,
          wpm: speed,
          bonusWordsHit: 2,
          springsUsed: 1,
          jetpacksUsed: 1,
          bonusWordsMissed: 0,
          missedWords: missed[i] ?? [],
          missedLetters: keys[i] ?? {},
        },
        new Date(start + i * day),
      );
    });
  }

  private readManifest(): AssetManifest | null {
    const raw: unknown = this.cache.json.get(MANIFEST_KEY);
    if (raw === null || typeof raw !== 'object') {
      console.warn('[Preload] assets/manifest.json missing or unreadable; using placeholders.');
      return null;
    }
    const candidate = raw as Partial<AssetManifest>;
    if (typeof candidate.groups !== 'object' || candidate.groups === null) return null;
    return {
      version: typeof candidate.version === 'number' ? candidate.version : 1,
      basePath: typeof candidate.basePath === 'string' ? candidate.basePath : 'assets',
      groups: candidate.groups,
    };
  }
}

/** A plausible level-1 run, for reviewing the Results layout in dev. */
function sampleRunStats(stars: number): GameRunStats {
  const accuracy = stars >= 3 ? 97 : stars === 2 ? 88 : 64;
  return {
    level: 1,
    platformCount: 20,
    reachedGoal: true,
    wordsAttempted: 18,
    wordsCorrect: 16,
    keystrokes: 74,
    keystrokeHits: Math.round(74 * (accuracy / 100)),
    accuracy,
    wordAccuracy: 89,
    longestStreak: 12,
    elapsedMs: 70400,
    score: 393,
    stars,
    wpm: 10,
    bonusWordsHit: 2,
    springsUsed: 2,
    jetpacksUsed: 0,
    bonusWordsMissed: 1,
    missedWords: ['brain', 'rainy'],
    missedLetters: { a: 2, r: 1, i: 1 },
    wordSlips: [
      { word: 'brain', typed: 'brian', missed: true, slips: 2 },
      { word: 'shell', typed: 'shell', missed: false, slips: 1 },
      { word: 'rainy', typed: 'rainey', missed: true, slips: 1 },
      { word: 'cozy', typed: 'cozy', missed: false, slips: 1 },
    ],
  };
}
